import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, GitCompare, ScanLine, Layers } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";

const PILLARS = [
  {
    icon: GitCompare,
    title: "Conciliação diária",
    body: "Batimento automático entre originador, bancarizador e custodiante. Arquivos retorno Dataprev × repasses CNPJ → FIDC, com matriz de divergências em D+1.",
  },
  {
    icon: Layers,
    title: "Carteiras consignadas CLT",
    body: "Cadastro estruturado de FIDCs e CNPJs devedores, com vínculo CPF do trabalhador, posições em aberto e enriquecimento via Receita + fontes públicas.",
  },
  {
    icon: ScanLine,
    title: "Fingerprint digital contínuo",
    body: "Monitoramento de pegada digital de CNPJ e CPF. Sinais de inadimplência, processos, mídia adversa e mudança de regime — antes do default.",
  },
  {
    icon: ShieldCheck,
    title: "Governança em três perfis",
    body: "Gestor do fundo, consultor especializado e originador, cada um com escopo próprio. Auditoria, retenção e versionamento sem fricção operacional.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col gradient-hero bg-background">
      <header className="container flex items-center justify-between h-20">
        <Logo />
        <Button asChild variant="outline" size="sm">
          <Link to="/login">
            Acessar plataforma <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </header>

      <section className="container flex-1 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center py-16">
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold tracking-wide text-primary-dark uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" /> FIDC · Consignado CLT
          </div>
          <h1 className="text-4xl lg:text-5xl font-display font-bold tracking-tight text-primary-dark text-balance">
            Monitoramento de risco diário para FIDCs de consignado CLT.
          </h1>
          <p className="text-lg text-muted-foreground text-balance max-w-xl">
            CredCapital concilia originador, bancarizador e custodiante todos os dias e mantém um fingerprint
            digital vivo dos CNPJs devedores — antes que o atraso vire perda.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button asChild size="lg">
              <Link to="/login">
                Entrar na plataforma <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <a href="#capacidades">Como funciona</a>
            </Button>
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary-medium/20 rounded-3xl blur-2xl" />
          <div className="relative rounded-3xl border border-border bg-card p-8 shadow-elevated space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Carteira em monitoramento</div>
                <div className="text-2xl font-display font-bold text-primary-dark">R$ 184,2M</div>
              </div>
              <div className="rounded-md bg-accent/15 text-accent-foreground px-3 py-1 text-xs font-semibold">D+0 conciliada</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Tile label="CNPJs ativos" value="312" />
              <Tile label="CPFs tomadores" value="14.806" />
              <Tile label="Divergências D-1" value="3" tone="warn" />
              <Tile label="Sinais críticos 7d" value="11" tone="critical" />
            </div>
            <div className="h-px bg-border" />
            <div className="text-xs text-muted-foreground">
              Painel ilustrativo · KPIs serão recalculados em produção a cada fechamento Dataprev.
            </div>
          </div>
        </div>
      </section>

      <section id="capacidades" className="bg-muted/60 border-y border-border">
        <div className="container py-20 space-y-10">
          <div className="max-w-2xl space-y-2">
            <div className="text-xs text-primary-medium font-semibold uppercase tracking-wider">Capacidades</div>
            <h2 className="text-3xl font-display font-bold text-primary-dark text-balance">
              Quatro frentes operando juntas para reduzir o gap entre evento e ação.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-xl border border-border bg-background p-6 shadow-card">
                <div className="h-10 w-10 rounded-md bg-primary-dark text-accent flex items-center justify-center mb-4">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-primary-dark mb-2">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="container py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-3">
          <Logo />
          <span>·</span>
          <span>© {new Date().getFullYear()} CredCapital</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="mailto:contato@credcapital.net" className="hover:text-primary-medium">contato@credcapital.net</a>
        </div>
      </footer>
    </div>
  );
}

function Tile({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "warn" | "critical" }) {
  const cls =
    tone === "critical"
      ? "border-destructive/30 bg-destructive/5"
      : tone === "warn"
      ? "border-accent/30 bg-accent/5"
      : "border-border bg-muted/40";
  return (
    <div className={`rounded-lg border ${cls} p-4`}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-xl font-display font-bold text-primary-dark">{value}</div>
    </div>
  );
}
